import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '../lib/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'erp_security_token';

// ── Token helpers ───────────────────────────────────────────────────────────
// Strategy: sessionStorage is the PRIMARY store (tab-scoped, auto-cleared on
// tab/browser close — more secure). localStorage is kept as a CROSS-TAB BACKUP
// so a new tab opened by the same user can inherit the active session without
// forcing a re-login. On logout BOTH are wiped.
const _setToken   = (t) => { sessionStorage.setItem(TOKEN_KEY, t); localStorage.setItem(TOKEN_KEY, t); };
const _getToken   = ()  => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
const _clearToken = ()  => { sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); };
// If the session tab has no token but localStorage has one (user opened a new
// tab), import it into sessionStorage so authFetch works from this tab too.
const _importCrossTabToken = () => {
  if (!sessionStorage.getItem(TOKEN_KEY)) {
    const ls = localStorage.getItem(TOKEN_KEY);
    if (ls) sessionStorage.setItem(TOKEN_KEY, ls);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSetup, setIsSetup] = useState(true);

  const checkSetup = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/auth/status`);
      const data = await res.json();
      setIsSetup(data.isSetup);
    } catch { setIsSetup(false); }
  }, []);

  const verifyToken = useCallback(async () => {
    _importCrossTabToken();            // inherit token from another tab if needed
    const token = _getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUser({ ...data, token });
      } else {
        _clearToken();
      }
    } catch { _clearToken(); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    checkSetup();
    verifyToken();
    // eslint-disable-next-line
  }, []);

  const login = (token, userData) => {
    sessionStorage.removeItem('erp-nav-subtab');
    sessionStorage.removeItem('erp-nav-subtab-ts');
    _setToken(token);                  // write to sessionStorage + localStorage
    setUser({ ...userData, token });
    setIsSetup(true);
  };

  const logout = async () => {
    try {
      const token = _getToken();
      if (token) {
        await fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch { /* swallow */ }
    _clearToken();                     // wipe BOTH sessionStorage and localStorage
    sessionStorage.removeItem('erp-nav-subtab');
    sessionStorage.removeItem('erp-nav-subtab-ts');
    setUser(null);
  };

  const clearMustChangePassword = () => {
    setUser(prev => prev ? ({ ...prev, mustChangePassword: false }) : prev);
  };

  const getToken = () => _getToken();

  return (
    <AuthContext.Provider value={{ user, loading, isSetup, setIsSetup, login, logout, getToken, checkSetup, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export { TOKEN_KEY, API };
